import React, { FunctionComponent, useCallback, useMemo } from "react";
import AssetsContext, { AssetsContextValue } from "contexts/AssetsContext";
import useFancyBearsInWallet from "hooks/useFancyBearsInWallet";
import useHoneyJarsInWallet from "hooks/useHoneyJarsInWallet";
import useConnectedAccount from "hooks/useConnectedAccount";
import { useBalance } from "wagmi";
import useHoneyBalance from "hooks/useHoneyBalance";
import useFetchTraitsInWallet from "hooks/useFetchTraitsInWallet";

const AssetsProvider: FunctionComponent = ({ children }) => {
  const [address] = useConnectedAccount();

  const [{ data: balanceData }] = useBalance({
    addressOrName: address,
  });

  const [honeyBalance, readHoneyBalance] = useHoneyBalance(address);

  const [fancyBearsInWallet, readFancyBearsInWallet] =
    useFancyBearsInWallet(address);

  const [honeyJarsInWallet, readHoneyJarsInWallet] =
    useHoneyJarsInWallet(address);

  const [traitsInWallet, fetchTraits] = useFetchTraitsInWallet(address);

  const fetchTraitsInWallet = useCallback(() => {
    if (address) {
      fetchTraits(address);
    }
  }, [address, fetchTraits]);

  const contextValue = useMemo<AssetsContextValue>(
    () => ({
      balance: balanceData?.value,
      formattedBalance: balanceData?.formatted,
      honeyBalance,
      fancyBearsInWallet,
      fancyBearsInWalletCount: fancyBearsInWallet?.length,
      honeyJarsInWallet,
      honeyJarsInWalletCount: honeyJarsInWallet?.length,
      traitsInWallet,
      traitsInWalletCount: traitsInWallet?.length,
      readFancyBearsInWallet,
      readHoneyJarsInWallet,
      readHoneyBalance,
      fetchTraitsInWallet,
    }),
    [
      honeyBalance,
      balanceData,
      fancyBearsInWallet,
      honeyJarsInWallet,
      traitsInWallet,
      readFancyBearsInWallet,
      readHoneyJarsInWallet,
      readHoneyBalance,
      fetchTraitsInWallet,
    ]
  );

  return (
    <AssetsContext.Provider value={contextValue}>
      {children}
    </AssetsContext.Provider>
  );
};

export default AssetsProvider;
